// Live-league hero HS4 — the band: one composite hero per
// genuinely gate-live league. Honest-empty: if the gate reports no
// live leagues, the band renders nothing (never a placeholder
// league). Each hero's data hook is gate-gated + soft-fails empty.

import type { BoardItem } from "../coverageOverview.ts";
import { LiveLeagueHero } from "./LiveLeagueHero.tsx";
import { useLiveLeagueHero } from "../useLiveLeagueHero.ts";

/** BoardItem.key for live items is `live:${sport}:${league_code}`.
 *  league_code slugs contain no ':' so slice(2).join is exact. */
export function liveLeagueCode(key: string): string | null {
  const parts = key.split(":");
  if (parts[0] !== "live" || parts.length < 3) return null;
  const code = parts.slice(2).join(":");
  return code.length > 0 ? code : null;
}

function LiveLeagueHeroContainer({
  code,
  name,
}: {
  code: string;
  name: string;
}): JSX.Element {
  const { players, fixtures } = useLiveLeagueHero(code, name);
  return (
    <LiveLeagueHero
      leagueCode={code}
      leagueName={name}
      players={players}
      fixtures={fixtures}
    />
  );
}

export function LiveLeagueBand({
  items,
}: {
  readonly items: readonly BoardItem[];
}): JSX.Element | null {
  const live = items
    .filter((i) => i.status === "live")
    .map((i) => ({ code: liveLeagueCode(i.key), name: i.title }))
    .filter((x): x is { code: string; name: string } => x.code !== null);

  if (live.length === 0) return null;

  return (
    <div
      data-testid="live-league-band"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
        gap: 14,
      }}
    >
      {live.map((l) => (
        <LiveLeagueHeroContainer key={l.code} code={l.code} name={l.name} />
      ))}
    </div>
  );
}
